import type {
  CatalogItemSourceView,
  CatalogMaterial,
  CatalogTool,
  InventoryItem,
  MaterialAcquisition,
  ProjectCatalog,
  ProjectDetail,
  StepNode,
  ToolAcquisition,
} from '@/api/types'

/**
 * Phase 16: which tier of the price priority a line resolved at.
 *
 * - {@code locked}: from an Acquisition's {@code lockedUnitPriceCents}.
 * - {@code live}: from {@code liveSource.lastFetchedPriceCents} on the catalog row's bound global.
 * - {@code default}: from the per-project {@code unitPriceCents} (the legacy fallback).
 *
 * The badge UI renders different colors per tier; the rollup carries this so totals + UI agree.
 */
export type PriceTier = 'locked' | 'live' | 'default'

export interface ResolvedPrice {
  tier: PriceTier
  unitPriceCents: number
  /** Set when {@code tier === 'live'} and the live source's {@code lastFetchedAt} is past the staleness threshold. */
  isStale: boolean
}

export const resolveMaterialPrice = (
  material: CatalogMaterial,
  acquisition?: MaterialAcquisition | null
): ResolvedPrice => {
  if (acquisition?.lockedUnitPriceCents != null) {
    return { tier: 'locked', unitPriceCents: acquisition.lockedUnitPriceCents, isStale: false }
  }
  const ls = material.liveSource
  if (ls && ls.lastFetchedPriceCents != null) {
    return { tier: 'live', unitPriceCents: ls.lastFetchedPriceCents, isStale: ls.isStale }
  }
  return { tier: 'default', unitPriceCents: material.unitPriceCents, isStale: false }
}

export const resolveToolPrice = (
  tool: CatalogTool,
  acquisition?: ToolAcquisition | null
): ResolvedPrice => {
  if (acquisition?.lockedUnitPriceCents != null) {
    return { tier: 'locked', unitPriceCents: acquisition.lockedUnitPriceCents, isStale: false }
  }
  const ls = tool.liveSource
  if (ls && ls.lastFetchedPriceCents != null) {
    return { tier: 'live', unitPriceCents: ls.lastFetchedPriceCents, isStale: ls.isStale }
  }
  return { tier: 'default', unitPriceCents: tool.unitPriceCents, isStale: false }
}

export interface BomMaterialLine {
  catalogMaterialId: string
  name: string
  unit: string
  /** Resolved unit price (locked > live > default). */
  unitPriceCents: number
  /** Phase 16: which tier the {@code unitPriceCents} resolved at. */
  priceTier: PriceTier
  /** Phase 16: true iff {@code priceTier === 'live'} and the source's last fetch is past staleness. */
  isLivePriceStale: boolean
  /** Phase 16: live source row, only set when {@code priceTier === 'live'}. */
  liveSource: CatalogItemSourceView | null
  totalQty: number
  /** Owned quantity matched from inventory by normalized (name, unit). 0 when no match. */
  ownedQty: number
  /** What the Doer still needs to buy: max(0, totalQty - ownedQty). Equals totalQty without inventory. */
  neededQty: number
  /** Cost line: neededQty × unitPriceCents. Equals totalQty × unitPriceCents without inventory. */
  lineTotalCents: number
}

export interface BomToolLine {
  catalogToolId: string
  name: string
  /** Resolved unit price (locked > live > default). */
  unitPriceCents: number
  priceTier: PriceTier
  isLivePriceStale: boolean
  liveSource: CatalogItemSourceView | null
  /**
   * True iff a {@link InventoryItem} exists with the same normalized name. Tools have no unit in
   * the catalog, so we match by name only; inventory rows for tools end up with whatever unit the
   * Doer chose ("ea" is conventional). When owned, the tool contributes 0 to the grand total.
   */
  owned: boolean
}

export interface BomRollup {
  materials: BomMaterialLine[]
  tools: BomToolLine[]
  /**
   * Sum of need-to-buy material line totals + sum of (deduped) unit prices of NOT-owned tools.
   * When called without inventory, this equals the project's full cost (legacy behavior).
   */
  grandTotalCents: number
}

const normalize = (value: string): string => value.trim().toLowerCase()

interface InventoryIndex {
  materialOwnedByKey: Map<string, number>
  toolOwnedByName: Set<string>
  /** Phase 15: ID-keyed maps for bound rows; preferred over name+unit lossy match. */
  materialOwnedByGlobalId: Map<string, number>
  toolOwnedByGlobalId: Set<string>
}

const buildInventoryIndex = (inventory: InventoryItem[]): InventoryIndex => {
  const materialOwnedByKey = new Map<string, number>()
  const toolOwnedByName = new Set<string>()
  const materialOwnedByGlobalId = new Map<string, number>()
  const toolOwnedByGlobalId = new Set<string>()
  for (const item of inventory) {
    const nameKey = normalize(item.name)
    const unitKey = normalize(item.unit)
    materialOwnedByKey.set(`${nameKey} ${unitKey}`, item.quantity)
    toolOwnedByName.add(nameKey)
    if (item.globalCatalogId && item.globalCatalogKind === 'MATERIAL') {
      materialOwnedByGlobalId.set(item.globalCatalogId, item.quantity)
    }
    if (item.globalCatalogId && item.globalCatalogKind === 'TOOL') {
      toolOwnedByGlobalId.add(item.globalCatalogId)
    }
  }
  return { materialOwnedByKey, toolOwnedByName, materialOwnedByGlobalId, toolOwnedByGlobalId }
}

/**
 * Aggregates material quantities and tool references across an arbitrary set of step subtrees.
 *
 * <p>Materials roll up by catalog id: same material attached to multiple steps sums into a single
 * line. Tools dedupe by catalog id: a tool used on five steps still costs you one drill. Grand
 * total = sum of material line totals + sum of (deduped) tool unit prices.
 *
 * <p>The walk is uniform across kinds: PROJECT_REFERENCE Steps own their snapshot's descendants
 * just like any Section, so they're traversed transparently — no per-kind branching required.
 *
 * <p>When {@code inventory} is supplied, the rollup is personalized: owned quantities are
 * subtracted from material lines (matched on normalized {@code (name, unit)} — same rule as the
 * {@code user_inventory_normalized_unique} index) and tools are flagged owned/not. This is the
 * mode the doing view runs in. Editor / viewer callers omit inventory and get the legacy
 * project-wide BOM.
 */
export const rollupBomFromSteps = (
  steps: StepNode[],
  catalog: ProjectCatalog,
  inventory: InventoryItem[] = []
): BomRollup => {
  const materialsById = new Map<string, CatalogMaterial>()
  for (const m of catalog.materials) materialsById.set(m.id, m)
  const toolsById = new Map<string, CatalogTool>()
  for (const t of catalog.tools) toolsById.set(t.id, t)

  const { materialOwnedByKey, toolOwnedByName, materialOwnedByGlobalId, toolOwnedByGlobalId } =
    buildInventoryIndex(inventory)

  const materialQty = new Map<string, number>()
  const toolIds = new Set<string>()

  const visit = (step: StepNode): void => {
    for (const sm of step.materials) {
      const prev = materialQty.get(sm.catalogMaterialId) ?? 0
      materialQty.set(sm.catalogMaterialId, prev + sm.quantity)
    }
    for (const st of step.tools) {
      toolIds.add(st.catalogToolId)
    }
    for (const child of step.children) visit(child)
  }
  for (const root of steps) visit(root)

  const materials: BomMaterialLine[] = []
  for (const [id, totalQty] of materialQty) {
    const material = materialsById.get(id)
    if (!material) continue
    // Phase 15: prefer ID-match when both the project's catalog row AND an Inventory row are
    // bound to the same global id. Falls back to the case-insensitive (name, unit) match for
    // unbound rows (Phase 3 behavior).
    let ownedQty = 0
    if (material.globalCatalogId && materialOwnedByGlobalId.has(material.globalCatalogId)) {
      ownedQty = materialOwnedByGlobalId.get(material.globalCatalogId) ?? 0
    } else {
      const key = `${normalize(material.name)} ${normalize(material.unit)}`
      ownedQty = materialOwnedByKey.get(key) ?? 0
    }
    const neededQty = Math.max(0, totalQty - ownedQty)
    // Phase 16: rollupBomFromSteps has no acquisitions in scope (editor / viewer rollup), so the
    // priority degrades to live > default.
    const resolved = resolveMaterialPrice(material, null)
    const lineTotalCents = Math.round(neededQty * resolved.unitPriceCents)
    materials.push({
      catalogMaterialId: id,
      name: material.name,
      unit: material.unit,
      unitPriceCents: resolved.unitPriceCents,
      priceTier: resolved.tier,
      isLivePriceStale: resolved.isStale,
      liveSource: resolved.tier === 'live' ? (material.liveSource ?? null) : null,
      totalQty,
      ownedQty,
      neededQty,
      lineTotalCents,
    })
  }
  materials.sort((a, b) => a.name.localeCompare(b.name))

  const tools: BomToolLine[] = []
  for (const id of toolIds) {
    const tool = toolsById.get(id)
    if (!tool) continue
    // Phase 15: same ID-first / name-fallback rule as materials.
    const ownedById = !!tool.globalCatalogId && toolOwnedByGlobalId.has(tool.globalCatalogId)
    const owned = ownedById || toolOwnedByName.has(normalize(tool.name))
    const resolved = resolveToolPrice(tool, null)
    tools.push({
      catalogToolId: id,
      name: tool.name,
      unitPriceCents: resolved.unitPriceCents,
      priceTier: resolved.tier,
      isLivePriceStale: resolved.isStale,
      liveSource: resolved.tier === 'live' ? (tool.liveSource ?? null) : null,
      owned,
    })
  }
  tools.sort((a, b) => a.name.localeCompare(b.name))

  const materialsTotal = materials.reduce((sum, line) => sum + line.lineTotalCents, 0)
  const toolsTotal = tools.reduce((sum, line) => sum + (line.owned ? 0 : line.unitPriceCents), 0)

  return {
    materials,
    tools,
    grandTotalCents: materialsTotal + toolsTotal,
  }
}

/** Project-wide rollup — convenience wrapper over rollupBomFromSteps. */
export const rollupBom = (project: ProjectDetail, inventory: InventoryItem[] = []): BomRollup =>
  rollupBomFromSteps(project.steps, project.catalog, inventory)

/**
 * Subtree-scoped rollup for a Milestone (or any single Step). Includes the step itself plus all
 * descendants. Catalog lookups go against the master Project's catalog — milestones don't have
 * their own.
 */
export const rollupBomForSubtree = (
  step: StepNode,
  catalog: ProjectCatalog,
  inventory: InventoryItem[] = []
): BomRollup => rollupBomFromSteps([step], catalog, inventory)

export const formatUsd = (cents: number): string => `$${(cents / 100).toFixed(2)}`

// ---- Phase 12: acquisitions-aware rollup ----------------------------------

export interface DriftSummary {
  /** From `following.startingBomTotalCents`. Null on pre-Phase-12 followings. */
  startingTotalCents: number | null
  /**
   * Sum over every acquired StepMaterial of (qty × (lockedUnitPrice ?? currentCatalogPrice))
   * + sum over every acquired catalog_tool_id of (lockedUnitPrice ?? currentCatalogPrice).
   * Tools only count once each (per-Project scope).
   */
  acquiredTotalCents: number
  /**
   * Sum at current catalog prices over every UN-acquired StepMaterial × needed-after-Inventory
   * quantity, plus every UN-acquired tool that's also not in Inventory. The "still need to
   * spend" number for the running budget.
   */
  stillNeededCents: number
  /**
   * (acquiredTotalCents + stillNeededCents) − startingTotalCents. Positive means the project
   * costs more now than the Doer committed to. Null when starting total is null.
   */
  driftCents: number | null
}

/**
 * Walks the snapshot tree against acquisitions + Inventory + current catalog prices and produces
 * the doing-view's drift display values. Pure function — same shape as `rollupBomFromSteps`
 * (Phase 13's DAG-aware rollup will replace both with a single graph traversal).
 */
export const computeDriftSummary = (
  steps: StepNode[],
  catalog: ProjectCatalog,
  inventory: InventoryItem[],
  materialAcquisitions: MaterialAcquisition[],
  toolAcquisitions: ToolAcquisition[],
  startingTotalCents: number | null
): DriftSummary => {
  const materialsById = new Map<string, CatalogMaterial>()
  for (const m of catalog.materials) materialsById.set(m.id, m)
  const toolsById = new Map<string, CatalogTool>()
  for (const t of catalog.tools) toolsById.set(t.id, t)

  const matAcqById = new Map<string, MaterialAcquisition>()
  for (const a of materialAcquisitions) matAcqById.set(a.snapshotAttachmentId, a)
  const toolAcqById = new Map<string, ToolAcquisition>()
  for (const a of toolAcquisitions) toolAcqById.set(a.catalogToolId, a)

  const { materialOwnedByKey, toolOwnedByName, materialOwnedByGlobalId, toolOwnedByGlobalId } =
    buildInventoryIndex(inventory)
  const inventoryRemaining = new Map<string, number>(materialOwnedByKey)
  const inventoryRemainingByGlobalId = new Map<string, number>(materialOwnedByGlobalId)

  let acquiredCents = 0
  let stillNeededCents = 0

  // Collect every distinct catalog_tool_id referenced anywhere in the tree (tools dedupe across
  // the whole project — same as rollupBomFromSteps).
  const referencedToolIds = new Set<string>()

  const walk = (step: StepNode): void => {
    for (const sm of step.materials) {
      const cat = materialsById.get(sm.catalogMaterialId)
      if (!cat) continue
      const acq = matAcqById.get(sm.id)
      if (acq) {
        // Phase 16 tier 1: locked-acquisition wins. {@code resolveMaterialPrice} returns the
        // locked price when an acquisition is supplied, falling back through live → default in
        // the rare case the locked price is null.
        const resolved = resolveMaterialPrice(cat, acq)
        acquiredCents += Math.round(sm.quantity * resolved.unitPriceCents)
      } else {
        // Phase 15: prefer ID-keyed inventory match; fall back to (name, unit).
        let consumed = 0
        if (cat.globalCatalogId && inventoryRemainingByGlobalId.has(cat.globalCatalogId)) {
          const remaining = inventoryRemainingByGlobalId.get(cat.globalCatalogId) ?? 0
          consumed = Math.min(sm.quantity, remaining)
          if (remaining > 0) {
            inventoryRemainingByGlobalId.set(cat.globalCatalogId, remaining - consumed)
          }
        } else {
          const key = `${normalize(cat.name)} ${normalize(cat.unit)}`
          const remaining = inventoryRemaining.get(key) ?? 0
          consumed = Math.min(sm.quantity, remaining)
          if (remaining > 0) inventoryRemaining.set(key, remaining - consumed)
        }
        const needed = sm.quantity - consumed
        if (needed > 0) {
          // Phase 16 tier 2/3: not acquired → live source > per-project default.
          const resolved = resolveMaterialPrice(cat, null)
          stillNeededCents += Math.round(needed * resolved.unitPriceCents)
        }
      }
    }
    for (const st of step.tools) referencedToolIds.add(st.catalogToolId)
    for (const child of step.children) walk(child)
  }
  for (const root of steps) walk(root)

  for (const id of referencedToolIds) {
    const cat = toolsById.get(id)
    if (!cat) continue
    const acq = toolAcqById.get(id)
    if (acq) {
      acquiredCents += resolveToolPrice(cat, acq).unitPriceCents
    } else {
      const ownedById = !!cat.globalCatalogId && toolOwnedByGlobalId.has(cat.globalCatalogId)
      if (!ownedById && !toolOwnedByName.has(normalize(cat.name))) {
        stillNeededCents += resolveToolPrice(cat, null).unitPriceCents
      }
    }
  }

  const driftCents =
    startingTotalCents == null ? null : acquiredCents + stillNeededCents - startingTotalCents

  return { startingTotalCents, acquiredTotalCents: acquiredCents, stillNeededCents, driftCents }
}

/**
 * Returns the snapshot_attachment_ids that have been orphaned by a refresh-from-source diff.
 * The diff carries `removedStepIds` etc.; we additionally need to detect StepMaterial entries
 * that no longer exist in the new snapshot. Because the frontend doesn't have access to the
 * new tree until after the refresh, this function takes the next-tree projection and walks it
 * for the set of surviving snapshot_attachment_ids — anything in `materialAcquisitions` that
 * isn't in the surviving set is orphaned.
 */
export const findOrphanedMaterialAcquisitions = (
  nextSteps: StepNode[],
  materialAcquisitions: MaterialAcquisition[]
): string[] => {
  const surviving = new Set<string>()
  const walk = (s: StepNode): void => {
    for (const sm of s.materials) surviving.add(sm.id)
    for (const c of s.children) walk(c)
  }
  for (const root of nextSteps) walk(root)
  return materialAcquisitions.map((a) => a.snapshotAttachmentId).filter((id) => !surviving.has(id))
}
